// src/pages/ProfileInputForm.jsx
import React, { useState, useCallback, useRef } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Snackbar,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery
} from "@mui/material";
import { ArrowBack as ArrowBackIcon, Refresh as RefreshIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

// Steps Components
import BasicInfoStep from "./components/steps/BasicInfoStep";
import PersonalDetailsStep from "./components/steps/PersonalDetailsStep";
import EducationProfessionStep from "./components/steps/EducationProfessionStep";
import FamilyHealthStep from "./components/steps/FamilyHealthStep";
import PreferencesStep from "./components/steps/PreferencesStep";
import StepNavigation from "./components/StepNavigation";

// Utilities - IMPORT THE NEW FUNCTION
import {
  WHATSAPP_NUMBER,
  getDefaultFormData,
  validateStep,
  formatProfileForWhatsApp  // Add this import
} from "./components/formUtils";

// Steps configuration with responsive labels
const stepConfig = [
  {
    full: "Basic Info",
    mobile: "Basic",
    tablet: "Basic Info"
  },
  {
    full: "Personal Details",
    mobile: "Personal",
    tablet: "Details"
  },
  {
    full: "Education & Profession",
    mobile: "Edu/Prof",
    tablet: "Education"
  },
  {
    full: "Family & Health",
    mobile: "Family",
    tablet: "Family"
  },
  {
    full: "Preferences",
    mobile: "Prefs",
    tablet: "Prefs"
  }
];

// Custom Step Icon with smaller numeric bullets
const CustomStepIcon = (props) => {
  const { active, completed, icon } = props;
  const theme = useTheme();

  return (
    <Box
      sx={{
        width: { xs: 24, sm: 28, md: 32 },
        height: { xs: 24, sm: 28, md: 32 },
        borderRadius: '50%',
        backgroundColor: completed
          ? theme.palette.success.main
          : active
            ? theme.palette.primary.main
            : theme.palette.grey[300],
        color: completed || active ? '#fff' : theme.palette.text.primary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
        fontWeight: 'bold',
        border: active ? `2px solid ${theme.palette.primary.main}` : 'none',
        boxShadow: active ? '0 0 0 3px rgba(25, 118, 210, 0.1)' : 'none',
        transition: 'all 0.3s ease'
      }}
    >
      {completed ? '✓' : icon}
    </Box>
  );
};

const ProfileInputForm = () => {
  const navigate = useNavigate();
  const formContentRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState(getDefaultFormData());

  const [notification, setNotification] = useState({ open: false, message: "", severity: "success" });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Inputs for array fields
  const [otherQualInput, setOtherQualInput] = useState("");
  const [hobbyInput, setHobbyInput] = useState("");
  const [sportInput, setSportInput] = useState("");
  const [profPrefInput, setProfPrefInput] = useState("");
  const [locPrefInput, setLocPrefInput] = useState("");

  // Get step label based on screen size
  const getStepLabel = (step) => {
    if (isMobile) return stepConfig[step].mobile;
    if (isTablet) return stepConfig[step].tablet;
    return stepConfig[step].full;
  };

  // ----------------- HANDLERS -----------------

  const handleInputChange = useCallback((path, value) => {
    const keys = path.split('.');
    setFormData(prev => {
      const newData = { ...prev };
      let current = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  }, []);

  const handleArrayAdd = useCallback((path, value, inputSetter) => {
    if (!value.trim()) return;

    const trimmedValue = value.trim();
    const keys = path.split('.');
    setFormData(prev => {
      const newData = { ...prev };
      let current = newData;
      for (let i = 0; i < keys.length - 1; i++) current = current[keys[i]];

      const currentArray = current[keys[keys.length - 1]];

      const exists = currentArray.some(item => item.toLowerCase() === trimmedValue.toLowerCase());
      if (!exists) {
        current[keys[keys.length - 1]] = [...currentArray, trimmedValue];
        if (inputSetter) inputSetter("");
      } else {
        setNotification({
          open: true,
          message: `"${trimmedValue}" already exists!`,
          severity: "warning"
        });
      }
      return newData;
    });
  }, []);

  const handleArrayRemove = useCallback((path, index) => {
    const keys = path.split('.');
    setFormData(prev => {
      const newData = { ...prev };
      let current = newData;
      for (let i = 0; i < keys.length - 1; i++) current = current[keys[i]];
      current[keys[keys.length - 1]] = current[keys[keys.length - 1]].filter((_, i) => i !== index);
      return newData;
    });
  }, []);

  // ----------------- STEP NAVIGATION -----------------
  const handleNext = () => {
    const { isValid, errors } = validateStep(activeStep, formData);
    if (isValid) {
      setActiveStep(prev => prev + 1);
      scrollToTop();
    } else {
      const firstError = Object.values(errors)[0];
      setNotification({ open: true, message: firstError, severity: "error" });
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
    scrollToTop();
  };

  const scrollToTop = () => {
    setTimeout(() => {
      if (formContentRef.current) formContentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  // ----------------- RESET FORM -----------------
  const handleRefreshForm = () => setConfirmDialogOpen(true);
  const handleConfirmRefresh = () => {
    setFormData(getDefaultFormData());
    setActiveStep(0);
    setOtherQualInput(""); setHobbyInput(""); setSportInput(""); setProfPrefInput(""); setLocPrefInput("");
    setNotification({ open: true, message: "Form has been reset successfully!", severity: "success" });
    setConfirmDialogOpen(false);
    scrollToTop();
  };
  const handleCancelRefresh = () => setConfirmDialogOpen(false);

  // ----------------- NOTIFICATION -----------------
  const handleCloseNotification = () => setNotification({ ...notification, open: false });

  // ----------------- WHATSAPP - UPDATED -----------------
  const shareOnWhatsApp = () => {
    // Use the new formatProfileForWhatsApp function instead of raw JSON.stringify
    const profileSummary = formatProfileForWhatsApp(formData);

    const encodedMessage = encodeURIComponent(profileSummary);
    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
    setNotification({ open: true, message: `Opening WhatsApp to send profile to ${WHATSAPP_NUMBER}`, severity: "success" });
  };

  // ----------------- STEP CONTENT -----------------
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return <BasicInfoStep formData={formData} handleInputChange={handleInputChange} />;
      case 1:
        return <PersonalDetailsStep formData={formData} handleInputChange={handleInputChange} />;
      case 2:
        return (
          <EducationProfessionStep
            formData={formData}
            handleInputChange={handleInputChange}
            otherQualInput={otherQualInput}
            setOtherQualInput={setOtherQualInput}
            handleArrayAdd={() => handleArrayAdd("education.other_qualifications", otherQualInput, setOtherQualInput)}
            handleArrayRemove={(i) => handleArrayRemove("education.other_qualifications", i)}
          />
        );
      case 3:
        return (
          <FamilyHealthStep
            formData={formData}
            handleInputChange={handleInputChange}
            hobbyInput={hobbyInput}
            setHobbyInput={setHobbyInput}
            sportInput={sportInput}
            setSportInput={setSportInput}
            handleArrayAddHobby={() => handleArrayAdd("habits_and_lifestyle.hobbies", hobbyInput, setHobbyInput)}
            handleArrayRemoveHobby={(i) => handleArrayRemove("habits_and_lifestyle.hobbies", i)}
            handleArrayAddSport={() => handleArrayAdd("habits_and_lifestyle.sports_interest", sportInput, setSportInput)}
            handleArrayRemoveSport={(i) => handleArrayRemove("habits_and_lifestyle.sports_interest", i)}
          />
        );
      case 4:
        return (
          <PreferencesStep
            formData={formData}
            handleInputChange={handleInputChange}
            profPrefInput={profPrefInput}
            setProfPrefInput={setProfPrefInput}
            locPrefInput={locPrefInput}
            setLocPrefInput={setLocPrefInput}
            handleArrayAddProfPref={() => handleArrayAdd("preferences.profession_preference", profPrefInput, setProfPrefInput)}
            handleArrayRemoveProfPref={(i) => handleArrayRemove("preferences.profession_preference", i)}
            handleArrayAddLocPref={() => handleArrayAdd("preferences.location_preference", locPrefInput, setLocPrefInput)}
            handleArrayRemoveLocPref={(i) => handleArrayRemove("preferences.location_preference", i)}
          />
        );
      default:
        return "Unknown step";
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 1, sm: 2, md: 3 } }}>
      <Paper elevation={3} sx={{
        p: { xs: 1.5, sm: 2.5, md: 3 },
        borderRadius: 2,
        overflow: 'hidden'
      }}>
        <Box ref={formContentRef} sx={{ mb: { xs: 2, sm: 3 } }}>
          {/* Header */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1.5, sm: 2 }
          }}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              fullWidth={isMobile}
              size={isMobile ? "small" : "medium"}
              sx={{
                maxWidth: { xs: '100%', sm: 'auto' },
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<RefreshIcon />}
              onClick={handleRefreshForm}
              fullWidth={isMobile}
              size={isMobile ? "small" : "medium"}
              sx={{
                maxWidth: { xs: '100%', sm: 'auto' },
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              }}
            >
              Reset Form
            </Button>
          </Box>

          <Typography
            variant={isMobile ? "h5" : "h4"}
            gutterBottom
            color="primary"
            fontWeight="bold"
            sx={{
              fontSize: {
                xs: '1.25rem',
                sm: '1.5rem',
                md: '1.75rem'
              },
              textAlign: 'center'
            }}
          >
            📋 Profile Input Form
          </Typography>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{
              fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
              textAlign: 'center'
            }}
          >
            Step {activeStep + 1} of {stepConfig.length} • Complete all steps to submit profile
          </Typography>
        </Box>

        {/* Responsive Stepper with Small Numeric Bullets */}
        <Box sx={{
          width: '100%',
          mb: { xs: 3, sm: 4 },
          overflowX: 'auto',
          '&::-webkit-scrollbar': {
            height: '4px'
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1'
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '2px'
          }
        }}>
          <Stepper
            activeStep={activeStep}
            alternativeLabel={!isMobile}
            sx={{
              minWidth: isMobile ? '500px' : 'auto',
              '& .MuiStep-root': {
                padding: { xs: '0 8px', sm: '0 12px' }
              },
              '& .MuiStepConnector-root': {
                top: { xs: 12, sm: 14, md: 16 }
              },
              '& .MuiStepConnector-line': {
                minHeight: '2px'
              }
            }}
          >
            {stepConfig.map((step, index) => (
              <Step key={step.full}>
                <StepLabel
                  StepIconComponent={CustomStepIcon}
                  sx={{
                    '& .MuiStepLabel-label': {
                      fontSize: {
                        xs: '0.65rem',
                        sm: '0.7rem',
                        md: '0.75rem',
                        lg: '0.8rem'
                      },
                      fontWeight: 500,
                      lineHeight: 1.2,
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                      overflow: 'visible',
                      marginTop: { xs: '6px', sm: '8px' },
                      color: activeStep === index ? theme.palette.primary.main : 'inherit'
                    },
                    '& .MuiStepLabel-label.Mui-active': {
                      fontWeight: 700,
                      color: theme.palette.primary.main
                    },
                    '& .MuiStepLabel-label.Mui-completed': {
                      color: theme.palette.success.main
                    }
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: 'block',
                      width: '100%',
                      padding: { xs: '0 2px', sm: '0 4px' }
                    }}
                  >
                    {getStepLabel(index)}
                  </Box>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Step Content */}
        <Box sx={{
          mt: { xs: 1, sm: 2 },
          mb: { xs: 3, sm: 4 },
          minHeight: {
            xs: '280px',
            sm: '350px',
            md: '400px'
          }
        }}>
          {getStepContent(activeStep)}
        </Box>

        {/* Navigation Buttons */}
        <StepNavigation
          activeStep={activeStep}
          steps={stepConfig.map(s => s.full)}
          handleBack={handleBack}
          handleNext={handleNext}
          shareOnWhatsApp={shareOnWhatsApp}
          WHATSAPP_NUMBER={WHATSAPP_NUMBER}
          isMobile={isMobile}
        />
      </Paper>

      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={handleCloseNotification}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'right'
        }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{
            width: '100%',
            fontSize: { xs: '0.75rem', sm: '0.875rem' }
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      {/* Refresh Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelRefresh}
        fullScreen={isMobile}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{
          fontSize: { xs: '1rem', sm: '1.25rem' },
          py: { xs: 1.5, sm: 2 },
          textAlign: 'center'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <RefreshIcon fontSize={isMobile ? "small" : "medium"} />
            Reset Form
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: { xs: 1, sm: 1.5 } }}>
          <DialogContentText
            component="div"
            sx={{
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              textAlign: 'center'
            }}
          >
            Are you sure you want to reset the entire form?
            <List dense sx={{
              mt: 1,
              mb: 1,
              '& .MuiListItem-root': {
                p: 0,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                justifyContent: 'center'
              }
            }}>
              <ListItem sx={{ display: 'list-item', textAlign: 'center' }}>
                <ListItemText primary="Clear all entered data" />
              </ListItem>
              <ListItem sx={{ display: 'list-item', textAlign: 'center' }}>
                <ListItemText primary="Reset to the first step" />
              </ListItem>
              <ListItem sx={{ display: 'list-item', textAlign: 'center' }}>
                <ListItemText primary="Generate a new Profile ID" />
              </ListItem>
            </List>
            <Typography
              component="div"
              variant="body2"
              color="error"
              sx={{
                mt: 1,
                fontWeight: 'bold',
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              }}
            >
              ⚠️ This action cannot be undone.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 1.5 },
          p: { xs: 1.5, sm: 2 },
          pt: 0.5
        }}>
          <Button
            onClick={handleCancelRefresh}
            color="secondary"
            variant="contained"
            fullWidth={isMobile}
            size={isMobile ? "small" : "medium"}
            sx={{
              m: 0,
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmRefresh}
            color="secondary"
            variant="contained"
            startIcon={<RefreshIcon />}
            fullWidth={isMobile}
            size={isMobile ? "small" : "medium"}
            sx={{
              m: 0,
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }}
          >
            Reset Form
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProfileInputForm;